import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { errMsg } from "../core/api";
import FileViewer from "./FileViewer";
import { useApp } from "../core/AppContext";
import Modal from "./Modal";
import Table from "./Table";
import Select from "./Select";
import FormFields, { buildBody, validateRequired } from "./FormFields";
import ResponsiveList from "./ResponsiveList";
import PrintReport from "./PrintReport";

/**
 * صفحه‌ی استاندارد لیست + فرم افزودن/ویرایش (مودال)
 *
 * props:
 *  title, subtitle, endpoint, columns, fields | getFields(form, ctx)
 *  canCreate, canEdit, canDelete, multipart (فرم با فایل)
 *  initial: مقادیر پیش‌فرض فرم، query: پارامتر ثابت لیست
 *  filters: [{ name, label, options }]
 *  createLabel, rowExtra(row) => دکمه‌های اضافه
 */
export default function RecordPage({
  title, subtitle, endpoint, columns, fields, getFields, canCreate, canEdit, canDelete, multipart,
  initial = {}, fixed = {}, query = {}, filters = [], createLabel = "افزودن", rowExtra, toForm, emptyText, headExtra, editFilter,
  transformBody, afterSave,hideFileButton,printReport
}) {
const { toast, confirm, meta } = useApp();
const [printRow, setPrintRow] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [fv, setFv] = useState({});
  const [modal, setModal] = useState(null); // { row|null }
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [formErr, setFormErr] = useState("");
  const [saving, setSaving] = useState(false);
const [viewer, setViewer] = useState(null);
  const qs = useMemo(() => JSON.stringify({ ...query, ...fv }), [query, fv]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr("");
    try {
      const params = Object.fromEntries(Object.entries(JSON.parse(qs)).filter(([, v]) => v));
      const { data } = await api.get(endpoint, { params });
      setRows(data.items || []);
    } catch (e) {
      setLoadErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint, qs]);
  useEffect(() => { load(); }, [load]);

  const activeFields = useMemo(() => (getFields ? getFields(form) : fields), [getFields, fields, form]);

  const openNew = () => { setForm({ ...initial }); setErrors({}); setFormErr(""); setModal({ row: null }); };
  const openEdit = (row) => {
    setForm(toForm ? toForm(row) : { ...row });
    setErrors({}); setFormErr(""); setModal({ row });
  };

  const submit = async () => {
    const editing = !!modal.row;
    const errs = validateRequired(activeFields.filter((f) => !f.adminOnlyCreate), form, { editing });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true); setFormErr("");
     try {
      const body = buildBody(activeFields, form, multipart);
      if (multipart && transformBody) transformBody(body, form);
      for (const [k, v] of Object.entries(fixed)) { if (multipart) body.append(k, v); else body[k] = v; }
      const res = editing ? await api.put(`${endpoint}/${modal.row.id}`, body) : await api.post(endpoint, body);
      if (afterSave) {
        try { await afterSave(res.data, form, { editing, row: modal.row }); }
        catch { toast("ثبت شد، اما بارگذاری برخی فایل‌ها ناموفق بود. از دکمه‌ی «فایل‌ها» دوباره اضافه کنید.", "err"); }
      }
      toast(editing ? "تغییرات ذخیره شد" : "با موفقیت ثبت شد");
      setModal(null);
      load();
    } catch (e) {
      setFormErr(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!(await confirm("این مورد حذف شود؟ این کار قابل بازگشت نیست."))) return;
    try {
      await api.delete(`${endpoint}/${row.id}`);
      toast("حذف شد");
      load();
    } catch (e) {
      toast(errMsg(e), "err");
    }
  };
const view = (row) => setViewer({ path: `${endpoint}/${row.id}/file`, name: title });

const showActions = canEdit || canDelete || multipart || rowExtra || printReport;

  return (
    <>
      <div className="page-head">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {headExtra}
          {canCreate && <button className="btn" onClick={openNew}><i className="fa-solid fa-plus" /> {createLabel}</button>}
        </div>
      </div>

      {filters.length > 0 && (
        <div className="toolbar">
          {filters.map((f) => (
            <div className="field" key={f.name}>
              <label>{f.label}</label>
              <Select value={fv[f.name] || ""} onChange={(v) => setFv((s) => ({ ...s, [f.name]: v }))}
                options={[{ value: "", label: "همه" }, ...f.options]} />
            </div>
          ))}
        </div>
      )}

      {loadErr && <div className="alert err">{loadErr} <button className="btn btn-sm btn-ghost" onClick={load}>تلاش دوباره</button></div>}
     <ResponsiveList
  columns={columns}
  rows={rows}
  loading={loading}
  empty={emptyText}
  
  actions={showActions ? (r) => (
    <>
    {printReport && (
  <button className="icon-btn" title="چاپ گزارش" onClick={() => setPrintRow(r)}>
    <i className="fa-solid fa-print" />
  </button>
)}
      {rowExtra && rowExtra(r, load)}
      {multipart && !hideFileButton && r.hasFile && <button className="icon-btn" title="مشاهده فایل" onClick={() => view(r)}><i className="fa-solid fa-paperclip" /></button>}
      {canEdit && (!editFilter || editFilter(r)) && <button className="icon-btn" title="ویرایش" onClick={() => openEdit(r)}><i className="fa-solid fa-pen" /></button>}
      {canDelete && <button className="icon-btn danger" title="حذف" onClick={() => remove(r)}><i className="fa-solid fa-trash" /></button>}
    </>
  ) : null}
  mobileTitle={(r) => columns[0]?.render ? columns[0].render(r) : r[columns[0]?.key] ?? "—"}
  mobileMeta={(r) => columns.slice(1).map((c) => {
    const v = c.render ? c.render(r) : (r[c.key] ?? "—");
    return { icon: c.icon, label: c.label, value: v, full: c.fullMobile };
  })}
/>

      {modal && (
        <Modal title={modal.row ? `ویرایش ${title}` : createLabel} onClose={() => !saving && setModal(null)}
          footer={
            <>
              <button className="btn" onClick={submit} disabled={saving}>{saving ? "در حال ذخیره..." : modal.row ? "ذخیره تغییرات" : "ثبت"}</button>
              <button className="btn btn-ghost" onClick={() => setModal(null)} disabled={saving}>انصراف</button>
            </>
          }>
          {formErr && <div className="alert err">{formErr}</div>}
          <FormFields fields={activeFields} form={form} setForm={setForm} errors={errors} />
        </Modal>
      )}

   {viewer && <FileViewer {...viewer} onClose={() => setViewer(null)} />}

    {printRow && <PrintReport {...printReport(printRow, meta)} onClose={() => setPrintRow(null)} />}
    </>
  );
}
